import { DNDFieldsEnum } from "./Types";
import { retailRetailSubCategoriesTooltip } from "./TooltipInformation";

/** Payload field keys for Retail → Retail Sub Categories (one level per row). */
export const RETAIL_RETAIL_SUB_CATEGORY_FIELD_NUMBER = "Sub Category Number";
export const RETAIL_RETAIL_SUB_CATEGORY_FIELD_NAME = "Sub Category Name";
/** @deprecated Legacy payload key; read when loading saved data. */
export const RETAIL_RETAIL_SUB_CATEGORY_FIELD_VALUE_LEGACY =
  "Sub Category Value";
export const RETAIL_RETAIL_SUB_CATEGORY_FIELD_ACTIVE = "Active";

export const RETAIL_RETAIL_SUB_CATEGORY_LEVELS = [1, 2, 3, 4, 5] as const;

export type RetailRetailSubCategoryLevel =
  (typeof RETAIL_RETAIL_SUB_CATEGORY_LEVELS)[number];

export const getRetailRetailSubCategoryLevelLabel = (
  level: RetailRetailSubCategoryLevel,
): string => `Level ${level}`;

export const RETAIL_RETAIL_SUB_CATEGORY_LEGACY_KEYS = [
  DNDFieldsEnum.SUBCATEGORY1,
  DNDFieldsEnum.SUBCATEGORY2,
  DNDFieldsEnum.SUBCATEGORY3,
  DNDFieldsEnum.SUBCATEGORY4,
  DNDFieldsEnum.SUBCATEGORY5,
] as const;

export const RETAIL_RETAIL_SUB_CATEGORY_SAMPLE_MARKER = "sample_test_agilysys";

export const RETAIL_RETAIL_SUB_CATEGORY_PAGE_TITLE = "Retail Sub Categories";
export const RETAIL_RETAIL_SUB_CATEGORY_LABEL_NUMBER = "Sub Category Number *";
export const RETAIL_RETAIL_SUB_CATEGORY_LABEL_NAME = "Sub Category Name *";
export const RETAIL_RETAIL_SUB_CATEGORY_LABEL_ACTIVE = "Active";
export const RETAIL_RETAIL_SUB_CATEGORY_SELECT_LEVEL_PLACEHOLDER =
  "Select level";
export const RETAIL_RETAIL_SUB_CATEGORY_LIST_TITLE =
  "Retail Sub Categories Available";
export const RETAIL_RETAIL_SUB_CATEGORY_EMPTY_LIST_MESSAGE =
  "No Retail Sub Category Information Available";
export const RETAIL_RETAIL_SUB_CATEGORY_BUTTON_SUFFIX = "Retail Sub Category";
export const RETAIL_RETAIL_SUB_CATEGORY_SAMPLE_WARNING =
  "Do not use sample data. Those are for demo purposes only!";

export const RETAIL_RETAIL_SUB_CATEGORY_TOOLTIPS = {
  header: retailRetailSubCategoriesTooltip.header,
  number: retailRetailSubCategoriesTooltip.level1,
  name: retailRetailSubCategoriesTooltip.level1,
  active: retailRetailSubCategoriesTooltip.active,
} as const;

export const RETAIL_RETAIL_SUB_CATEGORY_VALIDATION_MESSAGES = {
  numberMandatory: "Sub Category Number is mandatory",
  nameMandatory: "Sub Category Name is mandatory",
  duplicateEntry: "Cannot have duplicate entries",
} as const;

export type RetailRetailSubCategoryPayload = {
  [RETAIL_RETAIL_SUB_CATEGORY_FIELD_NUMBER]: RetailRetailSubCategoryLevel | "";
  [RETAIL_RETAIL_SUB_CATEGORY_FIELD_NAME]: string | null;
  [RETAIL_RETAIL_SUB_CATEGORY_FIELD_ACTIVE]: boolean;
};

export const rowContainsRetailRetailSubCategorySample = (obj: any): boolean =>
  Object.values(obj ?? {}).some((value: any) => {
    if (typeof value === "string") {
      return value
        .toLowerCase()
        .includes(RETAIL_RETAIL_SUB_CATEGORY_SAMPLE_MARKER);
    }
    if (Array.isArray(value)) {
      return value.some(
        (item) =>
          typeof item === "string" &&
          item.toLowerCase().includes(RETAIL_RETAIL_SUB_CATEGORY_SAMPLE_MARKER),
      );
    }
    return false;
  });

export const isRetailRetailSubCategorySampleValue = (value: unknown): boolean =>
  typeof value === "string" &&
  value.toLowerCase().includes(RETAIL_RETAIL_SUB_CATEGORY_SAMPLE_MARKER);

export const isRetailRetailSubCategoryActive = (
  record: Record<string, unknown>,
): boolean => {
  const active = record[RETAIL_RETAIL_SUB_CATEGORY_FIELD_ACTIVE];
  return active !== false && active !== "No" && active !== "no";
};

export const parseRetailRetailSubCategoryLevelFromRow = (
  list: Record<string, unknown> | null | undefined,
): RetailRetailSubCategoryLevel | "" => {
  const level = Number(list?.[RETAIL_RETAIL_SUB_CATEGORY_FIELD_NUMBER]);
  return RETAIL_RETAIL_SUB_CATEGORY_LEVELS.includes(
    level as RetailRetailSubCategoryLevel,
  )
    ? (level as RetailRetailSubCategoryLevel)
    : "";
};

export const isRetailRetailSubCategoryRowActive = (
  list: Record<string, unknown> | null | undefined,
): boolean => list?.[RETAIL_RETAIL_SUB_CATEGORY_FIELD_ACTIVE] !== false;

export const getRetailRetailSubCategoryNameFromRow = (
  record: Record<string, unknown> | null | undefined,
): string | null => {
  const name = record?.[RETAIL_RETAIL_SUB_CATEGORY_FIELD_NAME];
  if (name != null && String(name).trim()) {
    return String(name);
  }
  const legacy = record?.[RETAIL_RETAIL_SUB_CATEGORY_FIELD_VALUE_LEGACY];
  return legacy != null ? String(legacy) : null;
};

export const buildRetailRetailSubCategoryPayload = (
  subCategoryNumber: RetailRetailSubCategoryLevel | "",
  subCategoryName: string | null,
  active: boolean,
): RetailRetailSubCategoryPayload => ({
  [RETAIL_RETAIL_SUB_CATEGORY_FIELD_NUMBER]: subCategoryNumber,
  [RETAIL_RETAIL_SUB_CATEGORY_FIELD_NAME]: subCategoryName,
  [RETAIL_RETAIL_SUB_CATEGORY_FIELD_ACTIVE]: active,
});

export const isRetailRetailSubCategoryDuplicateEntry = (
  items: any[],
  number: RetailRetailSubCategoryLevel | "",
  value: string | null,
  excludeId?: string | null,
): boolean => {
  if (number === "" || !value?.trim()) {
    return false;
  }
  return items.some(
    (item: any) =>
      String(item.id) !== String(excludeId ?? "") &&
      Number(item[RETAIL_RETAIL_SUB_CATEGORY_FIELD_NUMBER]) ===
        Number(number) &&
      String(getRetailRetailSubCategoryNameFromRow(item) ?? "").trim() ===
        value.trim(),
  );
};

/** Display-only DND field key; not saved to payload. */
export const RETAIL_RETAIL_SUB_CATEGORY_DND_LEVEL_LABEL = "Sub Category Level";

export const formatRetailRetailSubCategoriesForDnd = (items: any[]): any[] =>
  items.map((item) => {
    const level = parseRetailRetailSubCategoryLevelFromRow(item);
    const name = getRetailRetailSubCategoryNameFromRow(item);
    return {
      ...item,
      [RETAIL_RETAIL_SUB_CATEGORY_FIELD_NAME]: name,
      [RETAIL_RETAIL_SUB_CATEGORY_DND_LEVEL_LABEL]:
        level !== ""
          ? getRetailRetailSubCategoryLevelLabel(level)
          : String(item[RETAIL_RETAIL_SUB_CATEGORY_FIELD_NUMBER] ?? ""),
    };
  });

export const stripRetailRetailSubCategoryDndDisplayFields = (
  items: any[],
): any[] =>
  items.map(
    ({ [RETAIL_RETAIL_SUB_CATEGORY_DND_LEVEL_LABEL]: _, ...rest }) => rest,
  );

const isFlatRetailRetailSubCategoryRow = (
  record: Record<string, unknown>,
): boolean => RETAIL_RETAIL_SUB_CATEGORY_FIELD_NUMBER in record;

/** Build level 1–5 option lists from flat or legacy retail sub category rows. */
export const buildRetailRetailSubCategoryOptionsByLevel = (
  rows: unknown[] | null | undefined,
): string[][] => {
  const buckets: string[][] = [[], [], [], [], []];
  const list = Array.isArray(rows) ? rows : [];

  for (const row of list) {
    if (!row || typeof row !== "object") {
      continue;
    }

    const record = row as Record<string, unknown>;
    if (!isRetailRetailSubCategoryActive(record)) {
      continue;
    }

    if (isFlatRetailRetailSubCategoryRow(record)) {
      const flatNum = record[RETAIL_RETAIL_SUB_CATEGORY_FIELD_NUMBER];
      const flatVal = getRetailRetailSubCategoryNameFromRow(record);
      if (
        flatNum != null &&
        flatVal != null &&
        String(flatVal).trim() &&
        !isRetailRetailSubCategorySampleValue(flatVal)
      ) {
        const idx = Number(flatNum) - 1;
        if (idx >= 0 && idx < 5) {
          buckets[idx].push(String(flatVal));
        }
      }
      continue;
    }

    RETAIL_RETAIL_SUB_CATEGORY_LEGACY_KEYS.forEach((key, index) => {
      const value = record[key];
      if (
        value != null &&
        String(value).trim() &&
        !isRetailRetailSubCategorySampleValue(value)
      ) {
        buckets[index].push(String(value));
      }
    });
  }

  return buckets;
};
